<template>
  <div class="container">
    <div class="f-jc w-100vw">
      <div class="f-v m-tx12 p-hx2">
        <donut-page-title title="Get started with Donut" />
        <div class="separator m-bx4" />
        <div class="t-lg bold dark-purple">Increase engagement with social connections</div>
        <donut-form action="/channel_programs">
          <div class="f m-tx1">
            <cdn-svg class="h-x3 m-rx1" path="nucleo/slack" :version="1" />
            <div class="t-xxl bold">Name your Donut Intros channel</div>
          </div>
          <div class="w-max-x76 m-tx1">
            <p v-emoji="description" class="t m-bx2" />
            <p v-if="props.displayShoutoutsCopy" v-emoji="shoutoutsDecription" class="t m-bx2" />
            <p class="t">
              <span> What do you want to call this channel? We suggest something like </span>
              <span class="bold">#virtual-coffee</span>
              <span>  or </span>
              <span class="bold">#coffee-buddies</span>
            </p>
          </div>
          <div class="m-tx2">
            <donut-input-new-channel
              v-if="isNewChannel"
              v-model="newChannelName"
              input-name="channel_name"
              @update-is-valid="updateNewChannelValid"
            />
            <donut-selector-prefabs-channel
              v-else
              v-model="existingChannel"
              form-name="channel_uid"
              :key-map="{ value: 'uid' }"
            />
          </div>
          <div class="t link c-p m-t" @click="() => isNewChannel = !isNewChannel">{{ isNewChannel ? 'Use an existing channel' : 'Create new channel' }}</div>
          <input v-for="(datum, i) in hiddenInputData" :key="`input-${i}`" type="hidden" :name="datum.name" :value="datum.value">
          <button type="submit" :class="{ disabled, 'm-tx2': true }">Submit</button>

          <div class="t-lg bold dark-purple m-tx3">Or want to set up something else?</div>
          <div class="f-sb m-tx2">
            <div v-for="button in programButtons" :key="button.title" class="modal-button" @click="openModal(button)">
              <div class="f">
                <lucide-icon :name="button.icon" class="t purple" :stroke-width="2" />
                <div class="t-lg bold m-l4">{{ button.title }}</div>
              </div>
              <div class="t-sm gray m-t12">{{ button.copy }}</div>
            </div>
          </div>
          <wizard-email-opt-in-checkbox class="m-tx4" />
        </donut-form>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, inject } from 'vue';
  import { useStore } from 'vuex';

  const props = defineProps({
    channelProgramBuildingBlocks: { type: Object, default: null },
    journeysData: { type: Object, default: null },
    shoutoutsData: { type: Object, default: null },
    displayShoutoutsCopy: { type: Boolean, deafult: false },
  });
  const { state, commit, dispatch } = useStore();
  const $i18n = inject('$i18n');

  const description = `Donut Intros works by introducing people from a specific Slack channel via group DM and encouraging them to ${state.messages.pairing_explanation_suffix}.`;
  const shoutoutsDecription = `Your team can use ${$i18n.t('shoutouts.feature')} for peer-to-peer recognition in this channel. `
    + 'Tag a teammate in Slack, shout out an achievement, and watch the productivity (and good vibes) roll in :trophy:.';

  const isNewChannel = ref(true);
  const newChannelName = ref('virtual-coffee');
  const existingChannel = ref(null);
  const newChannelValid = ref(false);
  const updateNewChannelValid = (val) => { newChannelValid.value = val; };
  const disabled = computed(() => {
    if (isNewChannel.value) return !newChannelValid.value;
    return !existingChannel.value;
  });

  const emailOptIn = ref(true);

  const hiddenInputData = computed(() => [
      { name: 'ref', value: new URLSearchParams(window.location.search).get('ref') },
      { name: 'success_on', value: 'form' },
      { name: 'program_type', value: 'c' },
    ]);

  const programButtons = computed(() => {
    const buttons = [
      { title: 'Journeys', icon: 'Route', copy: 'Onboarding, training, mentorship, etc...', component: 'wizard-journeys', props: props.journeysData },
      { title: 'Watercooler', icon: 'GlassWater', copy: 'Break the ice and keep conversation flowing', buildingBlockKey: 'watercooler' },
      { title: 'Celebrations', icon: 'Cake', copy: 'Automatically celebrate milestones', buildingBlockKey: 'celebrations' },
    ];
    if (props.shoutoutsData) {
      buttons.unshift({ title: $i18n.t('shoutouts.feature'), icon: 'Megaphone', copy: 'Celebrate wins and recognize great work', component: 'wizard-shoutouts', props: props.shoutoutsData });
    }
    return buttons;
  });
  const openModal = (button) => {
    window.donut.track('Wizard - Clicked Program Button', { program: button.title });
    if (button.component) {
      // If the button has a wizard specific subcomponent, open it in the wizard specific modal
      commit('updateModal', {
        visible: true,
        subcomponent: 'wizard-modals-other-program',
        size: 'xl',
        data: { ...button, emailOptIn: emailOptIn.value },
      });
    } else if (button.buildingBlockKey) {
      // If it has a CoffeeBuildingBlock record, call that specific modal opening action
      const buildingBlock = props.channelProgramBuildingBlocks[button.buildingBlockKey];
      const { icon, copy } = button;
      dispatch('openBuildingBlockPreviewModal', { buildingBlock, wizardHeader: { icon, copy } });
    }
  };
</script>

<style scoped lang="scss">
// TODO: Consider just using the card and card:hover classes
  @import '../../styles/donut';
  .modal-button {
    width: 144px;
    border: 1px solid $border;
    border-radius: 8px;
    padding: 8px;
    cursor: pointer;
  }
  .modal-button:hover {
    transform: translateY(-2px);
    box-shadow: -2px 2px 6px $border;
  }
</style>
